/**
 * Enhanced Orders Page with Socket.IO-based notifications
 * This example shows how to use the new real-time notification system
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useToast } from '../context/ToastContext';
import { customerOrdersApi } from '../api/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { ORDER_EVENTS } from '../constants/socketEvents';
import LoadingScreen from '../components/LoadingScreen';
import RealtimeNotificationBell from '../components/RealtimeNotificationBell';
import { DisplayContext, formatOrderId, getOrderTypeDisplay } from '../utils/orderFormatter';
import { CURRENCY } from '../config/currency';

// Item styling for modern theme
const itemKeywordStyles: { [key: string]: { emoji: string; color: string } } = {
  coffee: { emoji: '☕', color: 'var(--status-warn-text)' },
  cappuccino: { emoji: '☕', color: '#6F4E37' }, /* basil-todo */
  latte: { emoji: '☕', color: '#C4A484' }, /* basil-todo */
  tea: { emoji: '🍵', color: '#228B22' }, /* basil-todo */
  juice: { emoji: '🧃', color: 'var(--status-warn)' },
  orange: { emoji: '🍊', color: 'var(--status-warn)' },
  smoothie: { emoji: '🥤', color: '#FF69B4' }, /* basil-todo */
  pizza: { emoji: '🍕', color: '#FF6347' }, /* basil-todo */
  burger: { emoji: '🍔', color: '#CD853F' }, /* basil-todo */
  sandwich: { emoji: '🥪', color: '#DAA520' }, /* basil-todo */
  pasta: { emoji: '🍝', color: 'var(--status-warn)' },
  noodles: { emoji: '🍜', color: '#FFD700' }, /* basil-todo */
  biryani: { emoji: '🍛', color: '#FF8C00' }, /* basil-todo */
  curry: { emoji: '🍛', color: '#B8860B' }, /* basil-todo */
  chicken: { emoji: '🍗', color: '#D2691E' }, /* basil-todo */
  fish: { emoji: '🐟', color: '#4682B4' }, /* basil-todo */
  soup: { emoji: '🍲', color: '#FF7F50' }, /* basil-todo */
  salad: { emoji: '🥗', color: '#32CD32' }, /* basil-todo */
  caesar: { emoji: '🥗', color: '#32CD32' }, /* basil-todo */
  fries: { emoji: '🍟', color: '#FFD700' }, /* basil-todo */
  fry: { emoji: '🍗', color: '#D2691E' }, /* basil-todo */
  cake: { emoji: '🍰', color: '#FF69B4' }, /* basil-todo */
  icecream: { emoji: '🍨', color: '#FFB6C1' }, /* basil-todo */
  bruschetta: { emoji: '🥖', color: '#CD853F' }, /* basil-todo */
  bread: { emoji: '🍞', color: '#D2691E' }, /* basil-todo */
  default: { emoji: '🍽️', color: 'var(--accent-alt)' }
};

const getItemEmoji = (itemName: string): { emoji: string; color: string } => {
  const lowerName = itemName.toLowerCase();
  for (const [keyword, style] of Object.entries(itemKeywordStyles)) {
    if (keyword !== 'default' && lowerName.includes(keyword)) {
      return style;
    }
  }
  return itemKeywordStyles.default;
};

interface Order {
  id: number;
  customOrderId?: string;
  status: string;
  total?: number;
  createdAt: string;
  vendorName?: string;
  tableName?: string;
  floorName?: string;
  sectionName?: string;
  items: any[];
}

interface PaginatedOrdersResponse {
  content: Order[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
}

const OrdersWithRealtimeUpdates: React.FC = () => {
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [highlightedOrders, setHighlightedOrders] = useState<Set<number>>(new Set());
  const socket = useSocket();
  const connected = socket?.isConnected || false;

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // Track subscribed order IDs
  const subscribedOrderIds = useRef<Set<number>>(new Set());

  // Subscribe to order rooms when orders change - only for active orders
  useEffect(() => {
    if (socket && orders.length > 0) {
      // Only subscribe to orders that can still change status (not COMPLETED or CANCELLED)
      const activeStatuses = ['PENDING', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED'];
      orders.forEach(order => {
        if (!subscribedOrderIds.current.has(order.id) && activeStatuses.includes(order.status)) {
          console.log(`[OrdersPage] Subscribing to order ${order.id} (status: ${order.status})`);
          socket.subscribeToOrder(order.id);
          subscribedOrderIds.current.add(order.id);
        }
      });
    }
  }, [socket, orders]);

  // Set up Socket.IO listeners for order updates
  useEffect(() => {
    if (socket && user?.id) {
      // Listen for order updates
      const handleOrderUpdate = (data: any) => {
        console.log('[OrdersPage] Real-time order update received:', data);

        // Parse orderId as number for comparison (backend may send as string)
        const eventOrderId = typeof data.orderId === 'string' ? parseInt(data.orderId, 10) : data.orderId;

        // Update the order in the list
        setOrders(prevOrders => {
          const updatedOrders = prevOrders.map(order =>
            order.id === eventOrderId
              ? { ...order, ...data, status: data.status || order.status }
              : order
          );

          // If order not in list, fetch it
          const orderExists = prevOrders.some(o => o.id === eventOrderId);
          if (!orderExists && data.userId === user?.id) {
            fetchOrder(eventOrderId);
          }

          return updatedOrders;
        });

        // Highlight the updated order
        highlightOrder(eventOrderId);

        // Show notification
        showToast({
          message: `📦 Order #${data.orderId} status: ${data.status}`,
          kind: 'success',
          idKey: `order_status_${data.orderId}_${data.status}`,
          allowedPaths: ['/orders']
        });
      };

      socket.on(ORDER_EVENTS.UPDATED, handleOrderUpdate);
      socket.on(ORDER_EVENTS.STATUS_UPDATE, handleOrderUpdate);

      return () => {
        socket.off(ORDER_EVENTS.UPDATED, handleOrderUpdate);
        socket.off(ORDER_EVENTS.STATUS_UPDATE, handleOrderUpdate);

        // Unsubscribe from all order rooms
        subscribedOrderIds.current.forEach(orderId => {
          console.log(`[OrdersPage] Unsubscribing from order ${orderId}`);
          socket.unsubscribeFromOrder(orderId);
        });
        subscribedOrderIds.current.clear();
      };
    }
  }, [socket, user]);

  // Fetch single order
  const fetchOrder = async (orderId: number) => {
    try {
      const response = await customerOrdersApi.getOrders({ page: 0, size: 1 });
      const order = response.data.content.find((o: Order) => o.id === orderId);
      if (order) {
        setOrders(prev => {
          const exists = prev.some(o => o.id === orderId);
          if (!exists) {
            return [order, ...prev];
          }
          return prev.map(o => o.id === orderId ? order : o);
        });
        highlightOrder(orderId);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  // Highlight order temporarily
  const highlightOrder = (orderId: number) => {
    setHighlightedOrders(prev => new Set(prev).add(orderId));
    setTimeout(() => {
      setHighlightedOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }, 3000);
  };

  // Fetch initial orders (first page)
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customerOrdersApi.getOrders({
        page: 0,
        size: 20,
        dateFilter: 'all' // Changed from 'recent' to 'all' to show all historical orders
      });
      const data: PaginatedOrdersResponse = response.data;
      setOrders(data.content || []);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setHasMore(!data.last);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load more orders (next page)
  const loadMoreOrders = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const response = await customerOrdersApi.getOrders({
        page: nextPage,
        size: 20,
        dateFilter: 'all'
      });
      const data: PaginatedOrdersResponse = response.data;

      // Append new orders to existing ones
      setOrders(prev => [...prev, ...data.content]);
      setCurrentPage(data.page);
      setHasMore(!data.last);
    } catch (error) {
      console.error('Error loading more orders:', error);
      toast.error('Failed to load more orders');
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreOrders();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMoreOrders]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Refresh orders when page becomes visible (handles new orders placed in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[OrdersPage] Page became visible, refreshing orders');
        fetchOrders();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchOrders]);

  // Check for navigation state indicating a new order was just placed
  useEffect(() => {
    const state = location.state as { newOrder?: boolean; orderId?: number } | null;
    if (state?.newOrder) {
      console.log('[OrdersPage] New order placed, refreshing orders list');
      fetchOrders();
      // Clear the state to avoid re-fetching on subsequent renders
      window.history.replaceState({}, document.title);

      // If we have the orderId, subscribe to it
      if (state.orderId && socket) {
        socket.subscribeToOrder(state.orderId);
        subscribedOrderIds.current.add(state.orderId);
      }
    }
  }, [location.state, fetchOrders, socket]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return theme.warning;
      case 'ACCEPTED': return theme.components?.status?.accepted?.text || '#5B9BD5';
      case 'PREPARING': return theme.primary || 'var(--action)';
      case 'READY': return theme.success || '#16A34A';
      case 'COMPLETED': return theme.success || '#16A34A';
      case 'CANCELLED': return theme.danger || '#DC2626';
      default: return theme.gray[500];
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.background }}>
      {/* Toaster provided globally via ToastProvider */}
      
      {/* Header with Notification Bell */}
      <div className="sticky top-0 z-40 backdrop-blur-lg border-b" style={{ 
        backgroundColor: theme.card + 'ee',
        borderColor: theme.border 
      }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: theme.text }}>
              My Orders
            </h1>
            
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Connection Status */}
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span
                  className={`h-2 w-2 rounded-full ${connected ? 'animate-pulse' : ''}`}
                  style={{ backgroundColor: connected ? (theme.success || '#16A34A') : (theme.danger || '#DC2626') }}
                />
                <span className="hidden sm:inline" style={{ color: theme.gray[600] }}>
                  {connected ? 'Live Updates' : 'Reconnecting...'}
                </span>
                <span className="sm:hidden" style={{ color: theme.gray[600] }}>
                  {connected ? 'Live' : '...'}
                </span>
              </div>
              
              {/* Notification Bell */}
              <RealtimeNotificationBell />
            </div>
          </div>
        </div>
      </div>

      {/* Orders List - Mobile responsive padding */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        {/* Pagination Info */}
        {orders.length > 0 && (
          <div className="mb-4 text-center">
            <p className="text-sm" style={{ color: theme.gray[600] }}>
              Showing {orders.length} of {totalElements} orders
            </p>
          </div>
        )}

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg" style={{ color: theme.gray[500] }}>
              No orders yet
            </p>
            <p className="text-sm mt-2" style={{ color: theme.gray[400] }}>
              Your orders will appear here in real-time
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Optional header hint for mobile users */}
            <p className="text-xs text-gray-400 mb-2 text-center">
              Tap any order to view details and invoice →
            </p>
            
            {orders.map(order => (
              <div
                key={order.id}
                onClick={() => navigate(`/customer/order/${order.id}`)}
                className={`group rounded-lg p-4 sm:p-6 transition-all duration-300 cursor-pointer hover:shadow-md hover:border-gray-300 ${
                  highlightedOrders.has(order.id)
                    ? 'ring-4 animate-pulse'
                    : ''
                }`}
                style={{
                  backgroundColor: theme.card,
                  border: `1px solid ${theme.border}`,
                  ringColor: highlightedOrders.has(order.id) ? theme.primary : 'transparent'
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: theme.text }}>
                      <span className="text-xl" style={{ color: getOrderTypeDisplay(order.customOrderId).color }}>
                        {getOrderTypeDisplay(order.customOrderId).icon}
                      </span>
                      <span>
                        Order {formatOrderId(order.customOrderId || `#${order.id}`, DisplayContext.VENDOR, { excludeToday: true })}
                        {order.vendorName && <span style={{ color: theme.gray[500], fontWeight: 'normal' }}> ({order.vendorName})</span>}
                      </span>
                    </h3>
                    <p className="text-sm mt-1" style={{ color: theme.gray[500] }}>
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                    {order.tableName && (
                      <p className="text-sm mt-1 flex items-center gap-1" style={{ color: theme.primary }}>
                        <span>🍽️</span>
                        <span>
                          Table {order.tableName}
                          {order.sectionName && ` • ${order.sectionName}`}
                          {order.floorName && ` • ${order.floorName}`}
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="text-right flex-shrink-0 ml-4">
                    <div className="flex justify-end items-center gap-2">
                      <span
                        className="px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap"
                        style={{
                          backgroundColor: getStatusColor(order.status) + '20',
                          color: getStatusColor(order.status)
                        }}
                      >
                        {order.status}
                      </span>
                      {/* Right arrow to indicate more details */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-all duration-300 group-hover:translate-x-0.5"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <p className="text-lg font-bold mt-2" style={{ color: theme.text }}>
                      {CURRENCY.symbol}{order.total?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>

                {/* Order Items - Mobile responsive */}
                <div className={isModernTheme ? "space-y-2" : "space-y-2"}>
                  {order.items.map((item, index) => {
                    const itemName = item.itemName || item.name || '';
                    const itemStyle = isModernTheme ? getItemEmoji(itemName) : null;
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between py-2 ${isModernTheme ? 'px-3 rounded-lg' : 'border-t'}`}
                        style={isModernTheme && itemStyle ? {
                          backgroundColor: `${itemStyle.color}10`,
                          border: `1px solid ${itemStyle.color}20`
                        } : {
                          borderColor: theme.border
                        }}
                      >
                        <span className="flex-1 min-w-0 pr-2 flex items-center gap-2" style={{ color: theme.text }}>
                          {isModernTheme && itemStyle && (
                            <span className="text-xl flex-shrink-0">{itemStyle.emoji}</span>
                          )}
                          <span>
                            <span className={`block sm:inline ${isModernTheme ? 'font-medium' : ''}`}>{item.quantity}x </span>
                            <span className="block sm:inline truncate">{itemName}</span>
                          </span>
                        </span>
                        <span 
                          className="flex-shrink-0 font-semibold" 
                          style={{ color: isModernTheme && itemStyle ? itemStyle.color : theme.gray[600] }}
                        >
                          {CURRENCY.symbol}{((item.price || 0) * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Load More Trigger (Invisible) */}
            {hasMore && (
              <div
                ref={loadMoreRef}
                className="h-20 flex items-center justify-center"
              >
                {loadingMore && (
                  <div className="flex items-center gap-2" style={{ color: theme.gray[500] }}>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading more orders...</span>
                  </div>
                )}
              </div>
            )}

            {/* End of List Message */}
            {!hasMore && orders.length > 0 && (
              <div className="text-center py-8">
                <p className="text-sm" style={{ color: theme.gray[500] }}>
                  You've reached the end of your order history
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Real-time Update Indicator */}
      {connected && (
        <div
          className="fixed bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"
          style={{
            backgroundColor: theme.success + '20',
            border: `1px solid ${theme.success}`,
            color: theme.success
          }}
        >
          <span className="h-2 w-2 rounded-full animate-pulse" 
            style={{ backgroundColor: theme.success }} 
          />
          <span className="text-sm">Real-time updates active</span>
        </div>
      )}
    </div>
  );
};

export default OrdersWithRealtimeUpdates;